import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cancelAppointment,
  deleteBlocker,
  fetchEmployeeSchedule,
  getCurrentBusinessConfig,
  getAppointmentsByIds,
  getEmployeeWorkHours,
  updateEmployeeSchedule_WithBitStringData,
} from '../../../firebase/firebase-delegate';
import { extractWorkHours, fillRangeInBitString, isTimeFree } from '../../../time/timeUtils';
import {
  Appointment,
  AppointmentStatus,
  BitStringData,
  Employee,
  WorkHours,
  isActiveAppointment,
  isBlocker,
  occupiesSchedule,
} from '../../../types';
import { TIME_INTERVAL } from '../../../constants';
import { dayjs, Dayjs } from '../../../utils/dayjs-config';
import {
  applyAppointmentChange,
  appointmentsByEmployeeKey,
  employeeScheduleKey,
  currentEmployeeKey,
  ChangeMode,
  setScheduleDayBitstring,
  type AppointmentsByEmployeeQueryKey,
  type EmployeeScheduleQueryKey,
} from '../../../utils/cache-delegate';
import { useTenant } from '../../../tenancy/TenantContext';
import { convertBusinessHoursToWorkHours } from '../../../utils/businessHours';

const DEFAULT_CACHE_STALE_TIME = 1000 * 60 * 20;
const MISSING_WORK_HOURS_TEMPLATE_ERROR = 'No schedule template found';

const collectUniqueAppointmentIds = (appointmentsByDate: Employee['appointments']): string[] => {
  if (!appointmentsByDate) return [];
  const ids = new Set<string>();
  Object.values(appointmentsByDate).forEach((entry) => {
    if (!Array.isArray(entry)) return;
    entry.forEach((id) => {
      if (typeof id === 'string' && id.trim().length > 0) {
        ids.add(id);
      }
    });
  });
  return Array.from(ids).sort();
};

const removeIdsFromAppointmentIndex = (
  appointmentsByDate: Employee['appointments'] | undefined,
  appointmentsToRemove: Appointment[]
): Employee['appointments'] => {
  const next = { ...(appointmentsByDate ?? {}) };
  appointmentsToRemove.forEach((appointment) => {
    const dateKey = appointment.appointmentDate;
    const existing = Array.isArray(next[dateKey]) ? next[dateKey] : [];
    const remaining = existing.filter((id) => id !== appointment.id);
    if (remaining.length > 0) {
      next[dateKey] = remaining;
    } else {
      delete next[dateKey];
    }
  });
  return next;
};

const patchCancelledAppointmentsInList = (
  existing: Appointment[],
  cancelledAppointments: Appointment[]
): Appointment[] => {
  if (cancelledAppointments.length === 0) {
    return existing;
  }
  const cancelledById = new Map(cancelledAppointments.map((appointment) => [appointment.id, appointment]));
  return existing
    .map((appointment) => {
      const cancelledMatch = cancelledById.get(appointment.id);
      if (!cancelledMatch) {
        return appointment;
      }
      if (isBlocker(cancelledMatch)) {
        return null;
      }
      return { ...appointment, status: AppointmentStatus.CANCELLED };
    })
    .filter((appointment): appointment is Appointment => Boolean(appointment));
};

export interface UseEmployeeScheduleManagerParams {
  employee: Employee;
  selectedDate: Dayjs;
}

export const useEmployeeScheduleManager = ({
  employee,
  selectedDate,
}: UseEmployeeScheduleManagerParams) => {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [affectedAppointments, setAffectedAppointments] = useState<Appointment[]>([]);
  const [updatedSchedule, setUpdatedSchedule] = useState<BitStringData | null>(null);
  const [updatedDate, setUpdatedDate] = useState<Dayjs | null>(null);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);

  const dateKey = selectedDate.format('MMDDYYYY');
  const employeeId = employee.id;
  const employeeDisplayName = `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim() || 'Employee';

  const appointmentIdsForDate = useMemo(() => {
    const ids = employee.appointments?.[dateKey];
    if (!Array.isArray(ids)) {
      return [];
    }
    return ids.filter((id): id is string => typeof id === 'string' && id.trim().length > 0);
  }, [employee.appointments, dateKey]);

  const allEmployeeAppointmentIds = useMemo(
    () => collectUniqueAppointmentIds(employee.appointments),
    [employee.appointments]
  );

  const appointmentsQuery = useQuery<Appointment[]>({
    queryKey: ['employeeDayAppointments', tenantId, employeeId, dateKey, appointmentIdsForDate.join('|')],
    queryFn: async () => {
      if (appointmentIdsForDate.length === 0) {
        return [];
      }
      return getAppointmentsByIds(appointmentIdsForDate, tenantId, 'manage-schedule');
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  const scheduleQuery = useQuery<Map<string, string>>({
    queryKey: ['employeeSchedule', tenantId, employeeId],
    queryFn: () => fetchEmployeeSchedule(employeeId, tenantId),
    enabled: Boolean(employeeId),
    staleTime: DEFAULT_CACHE_STALE_TIME,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const workHoursQuery = useQuery<WorkHours>({
    queryKey: ['employeeWorkHours', tenantId, employeeId],
    queryFn: () => getEmployeeWorkHours(employeeId, tenantId),
    enabled: Boolean(employeeId),
    staleTime: DEFAULT_CACHE_STALE_TIME,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const businessConfigQuery = useQuery({
    queryKey: ['businessConfig', tenantId],
    queryFn: () => getCurrentBusinessConfig(tenantId),
    staleTime: DEFAULT_CACHE_STALE_TIME,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const isMissingWorkHoursTemplateError = useMemo(() => {
    if (!workHoursQuery.isError) {
      return false;
    }
    const message = workHoursQuery.error instanceof Error ? workHoursQuery.error.message : '';
    return message.includes(MISSING_WORK_HOURS_TEMPLATE_ERROR);
  }, [workHoursQuery.error, workHoursQuery.isError]);

  const fallbackWorkHours = useMemo(
    () => convertBusinessHoursToWorkHours(businessConfigQuery.data?.businessInfo?.businessHours),
    [businessConfigQuery.data?.businessInfo?.businessHours]
  );

  const workHoursTemplate = useMemo(() => {
    if (workHoursQuery.data) {
      return workHoursQuery.data;
    }
    if (isMissingWorkHoursTemplateError) {
      return fallbackWorkHours;
    }
    return null;
  }, [fallbackWorkHours, isMissingWorkHoursTemplateError, workHoursQuery.data]);

  const selectedDateAppointments = useMemo(() => {
    const sorted = [...(appointmentsQuery.data ?? [])];
    sorted.sort((a, b) => Number(a.appointmentTime) - Number(b.appointmentTime));
    return sorted;
  }, [appointmentsQuery.data]);

  const activeSelectedAppointments = useMemo(
    () => selectedDateAppointments.filter(isActiveAppointment),
    [selectedDateAppointments]
  );
  const blockerSelectedAppointments = useMemo(
    () => selectedDateAppointments.filter(isBlocker),
    [selectedDateAppointments]
  );
  const occupyingSelectedAppointments = useMemo(
    () => selectedDateAppointments.filter(occupiesSchedule),
    [selectedDateAppointments]
  );

  const workHours = useMemo(() => {
    const bitString = scheduleQuery.data?.get(dateKey) ?? null;
    if (!bitString) {
      return null;
    }
    return extractWorkHours(occupyingSelectedAppointments, bitString);
  }, [dateKey, occupyingSelectedAppointments, scheduleQuery.data]);

  const appointmentsQueryKey = useMemo<AppointmentsByEmployeeQueryKey | null>(
    () => (employeeId ? appointmentsByEmployeeKey(tenantId, employeeId, allEmployeeAppointmentIds) : null),
    [tenantId, employeeId, allEmployeeAppointmentIds]
  );
  const scheduleQueryKey = useMemo<EmployeeScheduleQueryKey | null>(
    () => (employeeId ? employeeScheduleKey(tenantId, employeeId) : null),
    [tenantId, employeeId]
  );

  const updateScheduleMutation = useMutation({
    mutationFn: async () => {
      if (!updatedDate || !updatedSchedule) {
        throw new Error('Missing required field');
      }
      await updateEmployeeSchedule_WithBitStringData(
        employeeId,
        updatedDate,
        updatedSchedule,
        scheduleQuery.data ?? undefined,
        tenantId
      );
    },
    onSuccess: async () => {
      setResultDialogOpen(true);
      if (updatedDate && updatedSchedule) {
        setScheduleDayBitstring(
          queryClient,
          tenantId,
          employeeId,
          updatedDate.format('MMDDYYYY'),
          updatedSchedule.bitString
        );
      }
      setUpdatedSchedule(null);
      setUpdatedDate(null);
    },
    onError: (error) => {
      console.error('Error updating schedule:', error);
      setResultDialogOpen(true);
    },
  });

  const cancelAffectedAppointmentsMutation = useMutation({
    mutationFn: async (appointmentsToCancel: Appointment[]) => {
      await Promise.all(
        appointmentsToCancel.map((appointment) =>
          isBlocker(appointment)
            ? deleteBlocker(appointment, tenantId)
            : cancelAppointment(appointment, undefined, scheduleQuery.data ?? undefined, tenantId)
        )
      );
      return appointmentsToCancel;
    },
    onSuccess: (_data, cancelledAppointments) => {
      const currentEmployeeId = queryClient.getQueryData<Employee | undefined>(currentEmployeeKey(tenantId))?.id;
      cancelledAppointments.forEach((appointment) => {
        applyAppointmentChange(
          queryClient,
          ChangeMode.REPLACE,
          { ...appointment, status: AppointmentStatus.CANCELLED },
          appointment,
          {
            siteId: tenantId,
            appointmentsQueryKey: appointmentsQueryKey ?? undefined,
            scheduleKey: scheduleQueryKey ?? undefined,
            // cancelAppointment/deleteBlocker already mutate Firestore schedule bitstrings.
            // Keep local cache in sync by cloning server-mutated state instead of re-applying
            // local bitstring math (which can re-occupy blocker slots on REPLACE).
            serverAlreadyUpdatedScheduleBitstrings: true,
            updateCurrentEmployeeIndex: appointment.employeeID === currentEmployeeId,
          }
        );
      });

      queryClient.setQueriesData<Appointment[]>(
        { queryKey: ['appointmentsByEmployee', tenantId] },
        (existing) => (existing ? patchCancelledAppointmentsInList(existing, cancelledAppointments) : existing)
      );
      queryClient.setQueriesData<Appointment[]>(
        { queryKey: ['employeeDayAppointments', tenantId] },
        (existing) => (existing ? patchCancelledAppointmentsInList(existing, cancelledAppointments) : existing)
      );
      queryClient.setQueriesData<Appointment[]>(
        { queryKey: ['employeeCalendarAppointments', tenantId] },
        (existing) => (existing ? patchCancelledAppointmentsInList(existing, cancelledAppointments) : existing)
      );
      queryClient.setQueriesData<Appointment[]>(
        { queryKey: ['teamDayAppointments', tenantId] },
        (existing) => (existing ? patchCancelledAppointmentsInList(existing, cancelledAppointments) : existing)
      );
      queryClient.setQueriesData<Appointment[]>(
        { queryKey: ['searchAppointmentsAdmin', tenantId] },
        (existing) => (existing ? patchCancelledAppointmentsInList(existing, cancelledAppointments) : existing)
      );

      queryClient.setQueryData<Employee | undefined>(currentEmployeeKey(tenantId), (existing) => {
        if (!existing) {
          return existing;
        }
        const ownedCancelledAppointments = cancelledAppointments.filter(
          (appointment) => appointment.employeeID === existing.id
        );
        if (ownedCancelledAppointments.length === 0) {
          return existing;
        }
        return {
          ...existing,
          appointments: removeIdsFromAppointmentIndex(existing.appointments, ownedCancelledAppointments),
        };
      });

      queryClient.setQueryData<Employee[] | undefined>(['employees', tenantId], (existing) => {
        if (!existing) {
          return existing;
        }
        return existing.map((employeeEntry) => {
          const ownedCancelledAppointments = cancelledAppointments.filter(
            (appointment) => appointment.employeeID === employeeEntry.id
          );
          if (ownedCancelledAppointments.length === 0) {
            return employeeEntry;
          }
          return {
            ...employeeEntry,
            appointments: removeIdsFromAppointmentIndex(employeeEntry.appointments, ownedCancelledAppointments),
          };
        });
      });

      updateScheduleMutation.mutate();
    },
    onError: (error) => {
      console.error('Error during cancellation:', error);
      setResultDialogOpen(true);
    },
  });

  useEffect(() => {
    if (!updatedSchedule) {
      return;
    }
    const newBitString = occupyingSelectedAppointments.reduce((bitString, appointment) => {
      const startTime = dayjs(appointment.appointmentTime, 'HHmm');
      const endTime = startTime.add(appointment.appointmentDuration, 'minute');
      return fillRangeInBitString(
        bitString,
        appointment.appointmentTime,
        endTime.format('HHmm'),
        true
      );
    }, updatedSchedule.bitString);

    if (newBitString !== updatedSchedule.bitString) {
      setUpdatedSchedule({
        ...updatedSchedule,
        bitString: newBitString,
      });
    }
  }, [updatedSchedule, occupyingSelectedAppointments]);

  const handleScheduleChange = (nextDate: Dayjs, nextWorkHours: BitStringData) => {
    if (cancelAffectedAppointmentsMutation.isPending || updateScheduleMutation.isPending) {
      return;
    }

    const scheduleConflicts = [...activeSelectedAppointments, ...blockerSelectedAppointments];
    const affectedApps = scheduleConflicts.filter((appointment) => {
      const hour = parseInt(appointment.appointmentTime.substring(0, 2), 10);
      const minute = parseInt(appointment.appointmentTime.substring(2, 4), 10);
      const startTime = nextDate.clone().hour(hour).minute(minute).second(0);
      const endTime = startTime.add(appointment.appointmentDuration, 'minute');

      let conflict = false;
      let currentTime = startTime;
      while (currentTime.isBefore(endTime)) {
        if (!isTimeFree(nextWorkHours.bitString, currentTime.hour() * 60 + currentTime.minute())) {
          conflict = true;
          break;
        }
        currentTime = currentTime.add(TIME_INTERVAL, 'minute');
      }
      return conflict;
    });

    setUpdatedSchedule(nextWorkHours);
    setUpdatedDate(nextDate);
    setAffectedAppointments(affectedApps);
    setConfirmationOpen(true);
  };

  const handleCancelConfirmation = () => {
    setConfirmationOpen(false);
    setUpdatedSchedule(null);
    setUpdatedDate(null);
  };

  const confirmScheduleChange = () => {
    if (cancelAffectedAppointmentsMutation.isPending || updateScheduleMutation.isPending) {
      return;
    }
    setConfirmationOpen(false);
    cancelAffectedAppointmentsMutation.mutate(affectedAppointments);
  };

  const onCloseResultDialog = () => {
    setResultDialogOpen(false);
  };

  const isBaseLoading =
    appointmentsQuery.isLoading ||
    appointmentsQuery.isFetching ||
    scheduleQuery.isLoading ||
    scheduleQuery.isFetching ||
    workHoursQuery.isLoading ||
    (isMissingWorkHoursTemplateError &&
      (businessConfigQuery.isLoading || businessConfigQuery.isFetching));

  const isSaving =
    cancelAffectedAppointmentsMutation.isPending ||
    updateScheduleMutation.isPending;

  const isSetUpError =
    appointmentsQuery.isError ||
    scheduleQuery.isError ||
    (isMissingWorkHoursTemplateError && businessConfigQuery.isError) ||
    (workHoursQuery.isError && !isMissingWorkHoursTemplateError);

  const setUpErrorMessage =
    appointmentsQuery.error?.message ||
    scheduleQuery.error?.message ||
    (isMissingWorkHoursTemplateError ? businessConfigQuery.error?.message : undefined) ||
    workHoursQuery.error?.message ||
    'Failed to load schedule data.';

  const mutationSuccess = cancelAffectedAppointmentsMutation.isSuccess && updateScheduleMutation.isSuccess;

  return {
    employeeId,
    employeeDisplayName,
    dateKey,
    isLoading: isBaseLoading,
    isSaving,
    isSetUpError,
    setUpErrorMessage,
    workHoursQuery,
    workHoursTemplate,
    workHours,
    selectedDateAppointments: activeSelectedAppointments,
    employeeAppointmentsMap: employee.appointments ?? {},
    handleScheduleChange,
    confirmationOpen,
    confirmScheduleChange,
    handleCancelConfirmation,
    affectedAppointments,
    resultDialogOpen,
    onCloseResultDialog,
    mutationSuccess,
  };
};
